import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Collapse from '@mui/material/Collapse';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import { observer } from 'mobx-react-lite';
import { useSnackbar } from 'notistack';
import React from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';

import { useCustomStore } from '../../../../hooks';
import {
  MotorClubTrafficIncident,
  MotorClubTrafficIncidentDriverLookupItem,
} from '../../../../store/api/motorClubMembership.api';
import AddTrafficIncidentModal, {
  AddTrafficIncidentPayload,
  TrafficIncidentDriverOption,
} from './AddTrafficIncidentModal';
import ViewTrafficIncidentModal from './ViewTrafficIncidentModal';

const PAGE_LIMIT = 20;
const INCIDENTS_SCROLL_CONTAINER_ID = 'motorclub-incidents-scroll';

type MembershipIncidentsGridProps = {
  openAddIncidentRequestKey?: number;
  showDriverView?: boolean;
};

const getIncidentStatusColors = (
  status: string,
): { bg: string; color: string } => {
  const normalized = status?.toLowerCase?.() || '';
  if (
    normalized.includes('case fee paid') ||
    normalized.includes('case fee accepted')
  ) {
    return {
      bg: '#E7F5EC',
      color: '#2F8F4E',
    };
  }
  if (
    normalized.includes('case reported to law firm') ||
    normalized.includes('case representation cancelled')
  ) {
    return {
      bg: '#F1E9FB',
      color: '#6C3FB0',
    };
  }
  if (
    normalized.includes('under review') ||
    normalized.includes('incident reported')
  ) {
    return {
      bg: '#FBEED3',
      color: '#B9790A',
    };
  }
  if (normalized.includes('member declined coverage')) {
    return {
      bg: '#E7EEFB',
      color: '#2358A8',
    };
  }

  return {
    bg: '#EEEEF0',
    color: '#66666F',
  };
};

const MembershipIncidentsGrid: React.FC<MembershipIncidentsGridProps> = ({
  openAddIncidentRequestKey,
  showDriverView,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const { motorClubMembershipStore } = useCustomStore();
  const [selectedStatus, setSelectedStatus] = React.useState<string | null>(
    null,
  );
  const [isAddIncidentModalOpen, setIsAddIncidentModalOpen] =
    React.useState(false);
  const [viewIncidentId, setViewIncidentId] = React.useState('');
  const [isIncidentDriversLoading, setIsIncidentDriversLoading] =
    React.useState(false);
  const [incidentDriverOptions, setIncidentDriverOptions] = React.useState<
    TrafficIncidentDriverOption[]
  >([]);
  const [expandedIncidents, setExpandedIncidents] = React.useState<
    Record<string, boolean>
  >({});
  const [subIncidents, setSubIncidents] = React.useState<
    Record<string, MotorClubTrafficIncident[]>
  >({});
  const [loadingSubIncidents, setLoadingSubIncidents] = React.useState<
    Record<string, boolean>
  >({});
  const [subIncidentErrors, setSubIncidentErrors] = React.useState<
    Record<string, string>
  >({});

  const incidents = motorClubMembershipStore.TrafficIncidents;

  const loadTrafficIncidents = React.useCallback(
    async (append = false) => {
      const responseOrErr = await motorClubMembershipStore.getTrafficIncidents({
        append,
        limit: PAGE_LIMIT,
      });

      if (responseOrErr?.isErr?.()) {
        enqueueSnackbar(responseOrErr.error.message, {
          variant: 'error',
        });
      }
    },
    [enqueueSnackbar, motorClubMembershipStore],
  );

  React.useEffect(() => {
    loadTrafficIncidents(false);
  }, [loadTrafficIncidents]);

  const filteredIncidents = React.useMemo(() => {
    if (!selectedStatus) return incidents;
    return incidents.filter((item) => item.status === selectedStatus);
  }, [incidents, selectedStatus]);

  const incidentStatuses = React.useMemo(() => {
    const uniqueStatuses = new Set<string>();
    incidents.forEach((item) => {
      if (item.status?.trim()) {
        uniqueStatuses.add(item.status);
      }
    });
    return Array.from(uniqueStatuses);
  }, [incidents]);

  React.useEffect(() => {
    if (selectedStatus && incidentStatuses.indexOf(selectedStatus) === -1) {
      setSelectedStatus(null);
    }
  }, [incidentStatuses, selectedStatus]);

  const loadIncidentDrivers = React.useCallback(async () => {
    setIsIncidentDriversLoading(true);
    const lookupOrErr =
      await motorClubMembershipStore.getTrafficIncidentDriversLookup();
    setIsIncidentDriversLoading(false);

    if (lookupOrErr?.isErr?.()) {
      enqueueSnackbar(lookupOrErr.error.message, {
        variant: 'error',
      });
      return;
    }

    const options: TrafficIncidentDriverOption[] =
      lookupOrErr.value?.items?.map(
        (item: MotorClubTrafficIncidentDriverLookupItem) => ({
          driverId: item.driverId,
          driverName: item.driverName,
        }),
      ) || [];

    setIncidentDriverOptions(options);
  }, [enqueueSnackbar, motorClubMembershipStore]);

  React.useEffect(() => {
    if (!isAddIncidentModalOpen || showDriverView) {
      return;
    }

    loadIncidentDrivers();
  }, [isAddIncidentModalOpen, showDriverView, loadIncidentDrivers]);

  React.useEffect(() => {
    if (!openAddIncidentRequestKey) {
      return;
    }

    setIsAddIncidentModalOpen(true);
  }, [openAddIncidentRequestKey]);

  const handleSubmitIncident = async (payload: AddTrafficIncidentPayload) => {
    const responseOrErr = await motorClubMembershipStore.addTrafficIncident({
      ackAttorney: payload.ackAttorney,
      ackMvr: payload.ackMvr,
      driverId: payload.driverId,
      incidentDate: payload.dateIssued,
      stateIssued: payload.stateIssued,
      statement: payload.statement,
    });

    if (responseOrErr.isErr()) {
      enqueueSnackbar(responseOrErr.error.message, {
        variant: 'error',
      });
      return false;
    }

    const incidentId = responseOrErr.value.incidentId || '';
    if (incidentId && payload.documents.length) {
      const uploadFormData = new FormData();
      payload.documents.forEach((file) => {
        uploadFormData.append('files', file);
      });

      uploadFormData.append(
        'data',
        JSON.stringify(
          payload.documents.map((_, index) => ({
            caseId: incidentId,
            fileName: `Traffic-Incident-${Date.now()}-${index + 1}`,
            fileType: 'Data Q',
            service: 'motor club traffic incident',
            type: 'account',
          })),
        ),
      );

      const uploadResponse =
        await motorClubMembershipStore.rootStore.apiStore.fileApi.uploadFile(
          uploadFormData,
        );

      if (uploadResponse.isErr()) {
        enqueueSnackbar(
          'Incident was submitted, but documents could not be uploaded.',
          {
            variant: 'warning',
          },
        );
      }
    }

    await loadTrafficIncidents(false);
    enqueueSnackbar('Traffic incident submitted successfully', {
      variant: 'success',
    });
    return true;
  };

  const toggleSubIncidents = async (incidentId: string) => {
    const isExpanded = !!expandedIncidents[incidentId];
    setExpandedIncidents((previous) => ({
      ...previous,
      [incidentId]: !isExpanded,
    }));

    if (isExpanded || loadingSubIncidents[incidentId]) {
      return;
    }

    setLoadingSubIncidents((previous) => ({ ...previous, [incidentId]: true }));
    setSubIncidentErrors((previous) => ({ ...previous, [incidentId]: '' }));

    const responseOrErr =
      await motorClubMembershipStore.getTrafficIncidentSubIncidents(incidentId);

    setLoadingSubIncidents((previous) => ({
      ...previous,
      [incidentId]: false,
    }));

    if (responseOrErr.isErr()) {
      setSubIncidentErrors((previous) => ({
        ...previous,
        [incidentId]: responseOrErr.error.message,
      }));
      return;
    }

    setSubIncidents((previous) => ({
      ...previous,
      [incidentId]: responseOrErr.value.items || [],
    }));
  };

  return (
    <Paper
      elevation={0}
      sx={{
        border: '1px solid #E2E2E6',
        borderRadius: '10px',
        overflow: 'hidden',
      }}>
      <Box
        sx={{
          alignItems: 'center',
          borderBottom: '1px solid #EEEEF0',
          display: 'flex',
          flexWrap: 'wrap',
          gap: '10px',
          justifyContent: 'space-between',
          padding: '18px 22px',
        }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
          <Box sx={{ alignItems: 'center', display: 'flex', gap: '8px' }}>
            <Typography
              sx={{
                color: '#1A1A1A',
                fontFamily: 'FiraSans-SemiBold',
                fontSize: '15.5px',
                lineHeight: '22px',
              }}>
              Reported Traffic Incidents
            </Typography>
            <Box
              sx={{
                backgroundColor: '#EEEEF0',
                borderRadius: '20px',
                color: '#66666F',
                fontFamily: 'FiraSans-SemiBold',
                fontSize: '11px',
                padding: '2px 8px',
              }}>
              {filteredIncidents.length}
            </Box>
          </Box>
          <Typography
            sx={{
              color: '#66666F',
              fontFamily: 'FiraSans-SemiBold',
              fontSize: '11.5px',
              lineHeight: '16px',
            }}>
            Once reported to the law firm, all case statuses and communications
            will come directly from the law firm.
          </Typography>
        </Box>

        <Button
          onClick={() => {
            setIsAddIncidentModalOpen(true);
          }}
          sx={{
            '&:hover': {
              backgroundColor: '#B69300',
            },
            backgroundColor: '#C9A200',
            borderRadius: '8px',
            color: '#1A1A1A',
            fontFamily: 'FiraSans-SemiBold',
            fontSize: '12.5px',
            minWidth: '150px',
            padding: '9px 15px',
            textTransform: 'none',
          }}>
          + Submit Incident
        </Button>
      </Box>

      <AddTrafficIncidentModal
        driverOptions={incidentDriverOptions}
        showDriverView={showDriverView}
        isLoadingDrivers={isIncidentDriversLoading}
        onClose={() => setIsAddIncidentModalOpen(false)}
        onSubmit={handleSubmitIncident}
        open={isAddIncidentModalOpen}
      />

      <ViewTrafficIncidentModal
        incidentId={viewIncidentId}
        onClose={() => setViewIncidentId('')}
        open={!!viewIncidentId}
      />

      <Box
        sx={{
          backgroundColor: '#F7F7F8',
          borderBottom: '1px solid #EEEEF0',
          display: 'flex',
          flexWrap: 'wrap',
          gap: '16px',
          padding: '14px 22px',
        }}>
        {incidentStatuses.map((status) => (
          <Box
            key={status}
            onClick={() => {
              setSelectedStatus((prev) => (prev === status ? null : status));
            }}
            sx={{
              alignItems: 'center',
              cursor: 'pointer',
              display: 'inline-flex',
              gap: '6px',
              opacity: selectedStatus && selectedStatus !== status ? 0.5 : 1,
            }}>
            <Box
              sx={{
                backgroundColor: getIncidentStatusColors(status).color,
                borderRadius: '50%',
                height: 8,
                width: 8,
              }}
            />
            <Typography
              sx={{
                color: '#66666F',
                fontFamily: 'FiraSans-SemiBold',
                fontSize: '11.5px',
                lineHeight: '16px',
              }}>
              {status}
            </Typography>
          </Box>
        ))}
      </Box>

      {motorClubMembershipStore.isTrafficIncidentsLoading &&
      !incidents.length ? (
        <Box
          sx={{
            alignItems: 'center',
            display: 'flex',
            justifyContent: 'center',
            minHeight: '140px',
          }}>
          <CircularProgress size={26} sx={{ color: '#DEC330' }} />
        </Box>
      ) : null}

      {!motorClubMembershipStore.isTrafficIncidentsLoading &&
      !incidents.length ? (
        <Box sx={{ padding: '18px 22px' }}>
          <Typography
            sx={{
              color: '#66666F',
              fontFamily: 'FiraSans-Regular',
              fontSize: '12.5px',
            }}>
            No incidents found.
          </Typography>
        </Box>
      ) : null}

      {incidents.length ? (
        <TableContainer
          id={INCIDENTS_SCROLL_CONTAINER_ID}
          sx={{
            maxHeight: '460px',
            overflowY: 'auto',
          }}>
          <InfiniteScroll
            dataLength={incidents.length}
            hasMore={motorClubMembershipStore.TrafficIncidentsHasMore}
            loader={
              <Box sx={{ py: 1.5, textAlign: 'center' }}>
                <CircularProgress size={22} sx={{ color: '#DEC330' }} />
              </Box>
            }
            next={() => {
              loadTrafficIncidents(true);
            }}
            scrollableTarget={INCIDENTS_SCROLL_CONTAINER_ID}
            style={{ overflow: 'unset' }}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={headerCellStyle}>INCIDENT NUMBER</TableCell>
                  {showDriverView ? null : (
                    <TableCell sx={headerCellStyle}>DRIVER</TableCell>
                  )}
                  <TableCell sx={headerCellStyle}>INCIDENT TYPE</TableCell>
                  <TableCell sx={headerCellStyle}>INCIDENT DATE</TableCell>
                  <TableCell sx={headerCellStyle}>INCIDENT STATE</TableCell>
                  <TableCell sx={headerCellStyle}>DATE SUBMITTED</TableCell>
                  <TableCell sx={headerCellStyle}>STATUS</TableCell>
                  <TableCell sx={headerCellStyle} />
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredIncidents.map((row) => {
                  const statusColors = getIncidentStatusColors(row.status);
                  const isExpanded = !!expandedIncidents[row.incidentId];
                  const childIncidents = subIncidents[row.incidentId] || [];

                  return (
                    <React.Fragment key={row.incidentId}>
                      <TableRow
                        sx={{
                          '&:hover': {
                            backgroundColor: '#F7F7F8',
                          },
                        }}>
                        <TableCell sx={bodyCellStyle}>
                          <Box
                            sx={{
                              alignItems: 'center',
                              display: 'flex',
                              gap: '4px',
                            }}>
                            <Button
                              aria-label={
                                isExpanded
                                  ? 'Collapse sub-incidents'
                                  : 'Expand sub-incidents'
                              }
                              onClick={() => toggleSubIncidents(row.incidentId)}
                              sx={{
                                color: '#66666F',
                                minWidth: '24px',
                                padding: 0,
                              }}>
                              {isExpanded ? (
                                <KeyboardArrowUpIcon sx={{ fontSize: 18 }} />
                              ) : (
                                <KeyboardArrowDownIcon sx={{ fontSize: 18 }} />
                              )}
                            </Button>
                            {row.number || '--'}
                          </Box>
                        </TableCell>
                        {showDriverView ? null : (
                          <TableCell sx={bodyCellStyle}>
                            {row.driver || '--'}
                          </TableCell>
                        )}
                        <TableCell sx={bodyCellStyle}>
                          {row.incidentType || '--'}
                        </TableCell>
                        <TableCell sx={bodyCellStyle}>
                          {row.dateIssued || '--'}
                        </TableCell>
                        <TableCell sx={bodyCellStyle}>
                          {row.state || '--'}
                        </TableCell>
                        <TableCell sx={bodyCellStyle}>
                          {row.dateSubmitted || '--'}
                        </TableCell>
                        <TableCell sx={bodyCellStyle}>
                          <Box
                            sx={{
                              alignItems: 'center',
                              backgroundColor: statusColors.bg,
                              borderRadius: '20px',
                              color: statusColors.color,
                              display: 'inline-flex',
                              fontFamily: 'FiraSans-SemiBold',
                              fontSize: '11.5px',
                              gap: '6px',
                              padding: '5px 10px',
                              whiteSpace: 'nowrap',
                            }}>
                            <span>{'●'}</span>
                            {row.status || 'Unknown'}
                          </Box>
                        </TableCell>
                        <TableCell sx={bodyCellStyle}>
                          <Button
                            onClick={() => {
                              setViewIncidentId(row.incidentId);
                            }}
                            sx={{
                              color: '#7D1935',
                              fontFamily: 'FiraSans-SemiBold',
                              fontSize: '12.5px',
                              minWidth: 'auto',
                              padding: 0,
                              textTransform: 'none',
                            }}>
                            View
                          </Button>
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell
                          colSpan={showDriverView ? 7 : 8}
                          sx={{ padding: 0 }}>
                          <Collapse
                            in={isExpanded}
                            timeout="auto"
                            unmountOnExit>
                            <Box sx={subIncidentContainerSx}>
                              {loadingSubIncidents[row.incidentId] ? (
                                <CircularProgress
                                  size={20}
                                  sx={{ color: '#DEC330' }}
                                />
                              ) : subIncidentErrors[row.incidentId] ? (
                                <Typography sx={subIncidentMessageSx}>
                                  {subIncidentErrors[row.incidentId]}
                                </Typography>
                              ) : childIncidents.length ? (
                                <Table size="small">
                                  <TableHead>
                                    <TableRow>
                                      <TableCell sx={subIncidentHeaderSx}>
                                        INCIDENT NUMBER
                                      </TableCell>
                                      <TableCell sx={subIncidentHeaderSx}>
                                        INCIDENT DATE
                                      </TableCell>
                                      <TableCell sx={subIncidentHeaderSx}>
                                        STATUS
                                      </TableCell>
                                      <TableCell sx={subIncidentHeaderSx}>
                                        VIEW
                                      </TableCell>
                                    </TableRow>
                                  </TableHead>
                                  <TableBody>
                                    {childIncidents.map((child) => (
                                      <TableRow key={child.incidentId}>
                                        <TableCell sx={bodyCellStyle}>
                                          {child.number || '--'}
                                        </TableCell>
                                        <TableCell sx={bodyCellStyle}>
                                          {child.dateIssued || '--'}
                                        </TableCell>
                                        <TableCell sx={bodyCellStyle}>
                                          {(() => {
                                            const childStatusColors =
                                              getIncidentStatusColors(
                                                child.status,
                                              );

                                            return (
                                              <Box
                                                sx={{
                                                  alignItems: 'center',
                                                  backgroundColor:
                                                    childStatusColors.bg,
                                                  borderRadius: '20px',
                                                  color:
                                                    childStatusColors.color,
                                                  display: 'inline-flex',
                                                  fontFamily:
                                                    'FiraSans-SemiBold',
                                                  fontSize: '11.5px',
                                                  gap: '6px',
                                                  padding: '5px 10px',
                                                  whiteSpace: 'nowrap',
                                                }}>
                                                <span>{'●'}</span>
                                                {child.status || 'Unknown'}
                                              </Box>
                                            );
                                          })()}
                                        </TableCell>
                                        <TableCell sx={bodyCellStyle}>
                                          <Button
                                            onClick={() =>
                                              setViewIncidentId(
                                                child.incidentId,
                                              )
                                            }
                                            sx={subIncidentViewButtonSx}>
                                            View
                                          </Button>
                                        </TableCell>
                                      </TableRow>
                                    ))}
                                  </TableBody>
                                </Table>
                              ) : (
                                <Typography sx={subIncidentMessageSx}>
                                  No sub-incidents found.
                                </Typography>
                              )}
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    </React.Fragment>
                  );
                })}
              </TableBody>
            </Table>
          </InfiniteScroll>
        </TableContainer>
      ) : null}
    </Paper>
  );
};

const headerCellStyle = {
  borderBottom: '1px solid #EEEEF0 !important',
  borderTop: '1px solid #EEEEF0',
  color: '#66666F',
  fontFamily: 'FiraSans-Bold',
  fontSize: '11px',
  fontWeight: 700,
  letterSpacing: '.4px',
  lineHeight: '16px',
  padding: '12px 22px !important',
  textAlign: 'left',
};

const bodyCellStyle = {
  borderBottom: '1px solid #EEEEF0',
  color: '#2B2B2B',
  fontFamily: 'FiraSans-Regular',
  fontSize: '11.5px',
};

const subIncidentContainerSx = {
  backgroundColor: '#FBFBFC',
  borderTop: '1px solid #EEEEF0',
  padding: '12px 22px 16px 54px',
};

const subIncidentHeaderSx = {
  ...headerCellStyle,
  backgroundColor: '#FBFBFC',
  padding: '8px 12px !important',
};

const subIncidentMessageSx = {
  color: '#66666F',
  fontFamily: 'FiraSans-Regular',
  fontSize: '12px',
};

const subIncidentViewButtonSx = {
  color: '#7D1935',
  fontFamily: 'FiraSans-SemiBold',
  fontSize: '12px',
  minWidth: 'auto',
  padding: 0,
  textTransform: 'none',
};

export default observer(MembershipIncidentsGrid);
